import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject, listAll } from 'firebase/storage';
import { getFirestore, collection, addDoc, getDocs, deleteDoc, doc, updateDoc, orderBy, query } from 'firebase/firestore';
import { initializeApp } from 'firebase/app';
import { useNavigate } from 'react-router-dom';
import { Upload, Trash2, Eye, X, Image as ImageIcon, FolderPlus, Edit3, Save, Download, GripVertical, ArrowUp, ArrowDown, Star } from 'lucide-react';

const firebaseConfig = {
  apiKey: "AIzaSyAGe4tEALPi6kdBnAJo4uf-8omOQ4Nda-0",
  authDomain: "pathway-academy.firebaseapp.com",
  projectId: "pathway-academy",
  storageBucket: "pathway-academy.firebasestorage.app",
  messagingSenderId: "822490806225",
  appId: "1:822490806225:web:218ebd85b334fa140312f0",
  measurementId: "G-43HW36L0TK"
};

const app = initializeApp(firebaseConfig);
const storage = getStorage(app);
const db = getFirestore(app);

const GalleryManagement = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectedImages, setSelectedImages] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [categories, setCategories] = useState(['general', 'events', 'facilities', 'students']);
  const [newCategory, setNewCategory] = useState('');
  const [editingImage, setEditingImage] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', category: 'general' });
  const [draggedImage, setDraggedImage] = useState(null);
  const [dragOverImage, setDragOverImage] = useState(null);
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/admin');
      }
    });

    fetchImages();

    return () => unsubscribe();
  }, [auth, navigate]);

  const fetchImages = async () => {
    try {
      const q = query(collection(db, 'gallery'), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const imageList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setImages(imageList);
    } catch (error) {
      console.error('Error fetching images:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (files) => {
    const fileArray = Array.from(files);
    
    for (const file of fileArray) {
      if (!file.type.startsWith('image/')) {
        alert(`${file.name} is not an image file`);
        continue;
      }

      const fileName = `${Date.now()}_${file.name}`;
      const storageRef = ref(storage, `gallery/${fileName}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      setUploadProgress(prev => ({ ...prev, [fileName]: 0 }));

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(prev => ({ ...prev, [fileName]: progress }));
        },
        (error) => {
          console.error('Upload failed:', error);
          setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[fileName];
            return newProgress;
          });
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            
            // Get the highest order value and add 1
            const maxOrder = images.length > 0 ? Math.max(...images.map(img => img.order || 0)) : -1;
            
            const imageData = {
              fileName: fileName,
              originalName: file.name,
              downloadURL: downloadURL,
              title: file.name.replace(/\.[^/.]+$/, ''),
              description: '',
              category: 'general',
              uploadedAt: new Date().toISOString(),
              size: file.size,
              type: file.type,
              order: maxOrder + 1,
              featuredOnHomepage: false
            };

            await addDoc(collection(db, 'gallery'), imageData);
            
            setUploadProgress(prev => {
              const newProgress = { ...prev };
              delete newProgress[fileName];
              return newProgress;
            });

            fetchImages();
          } catch (error) {
            console.error('Error saving image metadata:', error);
          }
        }
      );
    }
  };

  const handleDeleteImage = async (image) => {
    if (window.confirm(`Are you sure you want to delete "${image.title}"?`)) {
      try {
        const storageRef = ref(storage, `gallery/${image.fileName}`);
        await deleteObject(storageRef);
        
        await deleteDoc(doc(db, 'gallery', image.id));
        
        setImages(images.filter(img => img.id !== image.id));
      } catch (error) {
        console.error('Error deleting image:', error);
        alert('Error deleting image. Please try again.');
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedImages.length === 0) return;
    
    if (window.confirm(`Are you sure you want to delete ${selectedImages.length} selected images?`)) {
      for (const imageId of selectedImages) {
        const image = images.find(img => img.id === imageId);
        if (image) {
          try {
            const storageRef = ref(storage, `gallery/${image.fileName}`);
            await deleteObject(storageRef);
            await deleteDoc(doc(db, 'gallery', image.id));
          } catch (error) {
            console.error('Error deleting image:', error);
          }
        }
      }
      setSelectedImages([]);
      fetchImages();
    }
  };

  const handleBulkFeature = async (featured) => {
    if (selectedImages.length === 0) return;
    
    const action = featured ? 'feature' : 'unfeature';
    if (window.confirm(`Are you sure you want to ${action} ${selectedImages.length} selected images?`)) {
      try {
        const updatePromises = selectedImages.map(imageId => 
          updateDoc(doc(db, 'gallery', imageId), {
            featuredOnHomepage: featured,
            updatedAt: new Date().toISOString()
          })
        );
        
        await Promise.all(updatePromises);
        
        // Update local state
        setImages(images.map(img => 
          selectedImages.includes(img.id)
            ? { ...img, featuredOnHomepage: featured }
            : img
        ));
        
        setSelectedImages([]);
      } catch (error) {
        console.error('Error updating featured status:', error);
        alert('Error updating featured status. Please try again.');
      }
    }
  };

  const handleBulkCategorize = async (category) => {
    if (selectedImages.length === 0 || !category) return;
    
    if (window.confirm(`Are you sure you want to change the category of ${selectedImages.length} selected images to "${category}"?`)) {
      try {
        const updatePromises = selectedImages.map(imageId => 
          updateDoc(doc(db, 'gallery', imageId), {
            category: category,
            updatedAt: new Date().toISOString()
          })
        );
        
        await Promise.all(updatePromises);
        
        // Update local state
        setImages(images.map(img => 
          selectedImages.includes(img.id)
            ? { ...img, category: category }
            : img
        ));
        
        setSelectedImages([]);
      } catch (error) {
        console.error('Error updating category:', error);
        alert('Error updating category. Please try again.');
      }
    }
  };

  const handleEditImage = (image) => {
    setEditingImage(image.id);
    setEditForm({
      title: image.title || '',
      description: image.description || '',
      category: image.category || 'general'
    });
  };

  const handleSaveEdit = async () => {
    try {
      await updateDoc(doc(db, 'gallery', editingImage), {
        title: editForm.title,
        description: editForm.description,
        category: editForm.category,
        updatedAt: new Date().toISOString()
      });
      
      setEditingImage(null);
      fetchImages();
    } catch (error) {
      console.error('Error updating image:', error);
    }
  };

  const addNewCategory = () => {
    if (newCategory.trim() && !categories.includes(newCategory.trim().toLowerCase())) {
      setCategories([...categories, newCategory.trim().toLowerCase()]);
      setNewCategory('');
    }
  };

  const toggleImageSelection = (imageId) => {
    setSelectedImages(prev => 
      prev.includes(imageId) 
        ? prev.filter(id => id !== imageId)
        : [...prev, imageId]
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const filteredImages = (category) => {
    return category === 'all' ? images : images.filter(img => img.category === category);
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    
    const newImages = [...images];
    const temp = newImages[index];
    newImages[index] = newImages[index - 1];
    newImages[index - 1] = temp;
    
    await updateImagesOrder(newImages);
  };

  const handleMoveDown = async (index) => {
    if (index === images.length - 1) return;
    
    const newImages = [...images];
    const temp = newImages[index];
    newImages[index] = newImages[index + 1];
    newImages[index + 1] = temp;
    
    await updateImagesOrder(newImages);
  };

  const updateImagesOrder = async (newImages) => {
    try {
      // Update local state immediately for better UX
      setImages(newImages);
      
      // Update order in Firestore
      const updatePromises = newImages.map((image, index) => 
        updateDoc(doc(db, 'gallery', image.id), { order: index })
      );
      
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error updating image order:', error);
      alert('Error updating image order. Please try again.');
      // Revert on error
      fetchImages();
    }
  };

  const handleDragStart = (e, image, index) => {
    setDraggedImage({ image, index });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverImage(index);
  };

  const handleDragLeave = () => {
    setDragOverImage(null);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (!draggedImage || draggedImage.index === dropIndex) {
      setDraggedImage(null);
      setDragOverImage(null);
      return;
    }
    
    const newImages = [...images];
    const [removed] = newImages.splice(draggedImage.index, 1);
    newImages.splice(dropIndex, 0, removed);
    
    await updateImagesOrder(newImages);
    
    setDraggedImage(null);
    setDragOverImage(null);
  };

  const handleDragEnd = () => {
    setDraggedImage(null);
    setDragOverImage(null);
  };

  const toggleHomepageFeatured = async (image) => {
    try {
      const newFeaturedStatus = !image.featuredOnHomepage;
      await updateDoc(doc(db, 'gallery', image.id), {
        featuredOnHomepage: newFeaturedStatus,
        updatedAt: new Date().toISOString()
      });
      
      // Update local state
      setImages(images.map(img => 
        img.id === image.id 
          ? { ...img, featuredOnHomepage: newFeaturedStatus }
          : img
      ));
    } catch (error) {
      console.error('Error toggling homepage featured:', error);
      alert('Error updating featured status. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading gallery...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Sticky Header */}
        <div className="sticky top-0 z-20 bg-gray-100 pb-4 pt-2 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-3xl font-bold text-gray-900">Gallery Management</h1>
          </div>
          
          {/* Bulk Action Buttons - Always visible when items selected */}
          {selectedImages.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-4 border-2 border-[#218581]">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">
                    {selectedImages.length} image{selectedImages.length !== 1 ? 's' : ''} selected
                  </span>
                  <button
                    onClick={() => setSelectedImages([])}
                    className="text-sm text-gray-500 hover:text-gray-700 underline"
                  >
                    Clear selection
                  </button>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => handleBulkFeature(true)}
                    className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 flex items-center gap-2 transition-colors"
                  >
                    <Star className="h-4 w-4" />
                    Feature
                  </button>
                  <button
                    onClick={() => handleBulkFeature(false)}
                    className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 flex items-center gap-2 transition-colors"
                  >
                    <Star className="h-4 w-4" />
                    Unfeature
                  </button>
                  <div className="relative">
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          handleBulkCategorize(e.target.value);
                          e.target.value = ''; // Reset dropdown
                        }
                      }}
                      className="px-4 py-2 bg-[#218581] text-white rounded-md hover:bg-[#1a6964] cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#218581] focus:ring-offset-2 appearance-none pr-8"
                      defaultValue=""
                    >
                      <option value="" disabled>Categorize...</option>
                      {categories.map(category => (
                        <option key={category} value={category} className="capitalize">
                          {category}
                        </option>
                      ))}
                    </select>
                    <FolderPlus className="h-4 w-4 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-white" />
                  </div>
                  <button
                    onClick={handleBulkDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-2 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Upload Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Upload Images
          </h2>
          
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <input
              type="file"
              id="imageUpload"
              multiple
              accept="image/*"
              onChange={(e) => handleFileUpload(e.target.files)}
              className="hidden"
            />
            <label htmlFor="imageUpload" className="cursor-pointer">
              <ImageIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-lg font-medium text-gray-900">Click to upload images</p>
              <p className="text-gray-500">or drag and drop images here</p>
              <p className="text-sm text-gray-400 mt-2">PNG, JPG, GIF up to 10MB each</p>
            </label>
          </div>

          {/* Upload Progress */}
          {Object.keys(uploadProgress).length > 0 && (
            <div className="mt-6 space-y-3">
              {Object.entries(uploadProgress).map(([fileName, progress]) => (
                <div key={fileName} className="bg-gray-50 rounded p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">{fileName}</span>
                    <span className="text-sm text-gray-500">{Math.round(progress)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-[#218581] h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Category Management */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <FolderPlus className="h-5 w-5" />
            Categories
          </h2>
          
          <div className="flex flex-wrap gap-2 mb-4">
            {categories.map(category => (
              <span key={category} className="px-3 py-1 bg-[#218581] text-white rounded-full text-sm capitalize">
                {category}
              </span>
            ))}
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="Add new category"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#218581]"
              onKeyPress={(e) => e.key === 'Enter' && addNewCategory()}
            />
            <button
              onClick={addNewCategory}
              className="px-4 py-2 bg-[#218581] text-white rounded-md hover:bg-[#1a6964]"
            >
              Add
            </button>
          </div>
        </div>

        {/* Images Grid */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-6">
            Gallery Images ({images.length})
          </h2>

          {/* Category Filter Tabs */}
          <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200">
            <button className="px-4 py-2 text-[#218581] border-b-2 border-[#218581] font-medium">
              All ({images.length})
            </button>
            {categories.map(category => {
              const count = filteredImages(category).length;
              return (
                <button key={category} className="px-4 py-2 text-gray-600 hover:text-[#218581] capitalize">
                  {category} ({count})
                </button>
              );
            })}
          </div>

          {images.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <p className="text-xl font-medium text-gray-500">No images uploaded yet</p>
              <p className="text-gray-400">Upload your first images to get started</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
              {images.map((image, index) => (
                <div 
                  key={image.id} 
                  draggable
                  onDragStart={(e) => handleDragStart(e, image, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all cursor-move ${
                    draggedImage?.index === index ? 'opacity-50' : ''
                  } ${
                    dragOverImage === index && draggedImage?.index !== index ? 'border-[#218581] border-2 ring-2 ring-[#218581] ring-opacity-50' : ''
                  } ${
                    image.featuredOnHomepage ? 'ring-2 ring-yellow-400' : ''
                  }`}
                >
                  {/* Ordering Controls */}
                  <div className="absolute top-1/2 left-1 -translate-y-1/2 z-10 flex flex-col gap-1 bg-white bg-opacity-90 rounded p-1">
                    <button
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      className={`p-1 rounded hover:bg-gray-100 transition-colors ${
                        index === 0 ? 'opacity-30 cursor-not-allowed' : 'text-gray-600 hover:text-[#218581]'
                      }`}
                      title="Move up"
                    >
                      <ArrowUp className="h-3 w-3" />
                    </button>
                    <GripVertical className="h-3 w-3 text-gray-400" />
                    <button
                      onClick={() => handleMoveDown(index)}
                      disabled={index === images.length - 1}
                      className={`p-1 rounded hover:bg-gray-100 transition-colors ${
                        index === images.length - 1 ? 'opacity-30 cursor-not-allowed' : 'text-gray-600 hover:text-[#218581]'
                      }`}
                      title="Move down"
                    >
                      <ArrowDown className="h-3 w-3" />
                    </button>
                  </div>

                  {/* Image */}
                  <div className="aspect-square relative">
                    <img
                      src={image.downloadURL}
                      alt={image.title}
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Selection checkbox */}
                    <div className="absolute top-2 left-2">
                      <input
                        type="checkbox"
                        checked={selectedImages.includes(image.id)}
                        onChange={() => toggleImageSelection(image.id)}
                        className="w-4 h-4 text-[#218581] border-gray-300 rounded focus:ring-[#218581]"
                      />
                    </div>

                    {/* Featured Badge */}
                    {image.featuredOnHomepage && (
                      <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                        <Star className="h-3 w-3 fill-current" />
                        Featured
                      </div>
                    )}

                    {/* Actions overlay */}
                    <div className="absolute top-2 right-2 flex gap-1">
                      <button
                        onClick={() => toggleHomepageFeatured(image)}
                        className={`p-1.5 rounded-full shadow-sm ${
                          image.featuredOnHomepage 
                            ? 'bg-yellow-400 hover:bg-yellow-500' 
                            : 'bg-white bg-opacity-90 hover:bg-opacity-100'
                        }`}
                        title={image.featuredOnHomepage ? 'Remove from homepage' : 'Feature on homepage'}
                      >
                        <Star className={`h-4 w-4 ${
                          image.featuredOnHomepage ? 'text-yellow-900 fill-current' : 'text-gray-600'
                        }`} />
                      </button>
                      <button
                        onClick={() => setPreviewImage(image)}
                        className="p-1.5 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full shadow-sm"
                      >
                        <Eye className="h-4 w-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => handleEditImage(image)}
                        className="p-1.5 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full shadow-sm"
                      >
                        <Edit3 className="h-4 w-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => handleDeleteImage(image)}
                        className="p-1.5 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full shadow-sm"
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </button>
                    </div>
                  </div>

                  {/* Image info */}
                  <div className="p-3">
                    {editingImage === image.id ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={editForm.title}
                          onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                          className="w-full text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#218581]"
                          placeholder="Image title"
                        />
                        <textarea
                          value={editForm.description}
                          onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                          className="w-full text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#218581]"
                          placeholder="Description"
                          rows={2}
                        />
                        <select
                          value={editForm.category}
                          onChange={(e) => setEditForm({...editForm, category: e.target.value})}
                          className="w-full text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#218581]"
                        >
                          {categories.map(cat => (
                            <option key={cat} value={cat} className="capitalize">{cat}</option>
                          ))}
                        </select>
                        <div className="flex gap-1">
                          <button
                            onClick={handleSaveEdit}
                            className="flex-1 px-2 py-1 bg-[#218581] text-white rounded text-sm hover:bg-[#1a6964]"
                          >
                            <Save className="h-3 w-3 inline mr-1" />
                            Save
                          </button>
                          <button
                            onClick={() => setEditingImage(null)}
                            className="flex-1 px-2 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <h3 className="font-medium text-gray-900 text-sm truncate" title={image.title}>
                          {image.title || image.originalName}
                        </h3>
                        {image.description && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{image.description}</p>
                        )}
                        <div className="flex justify-between items-center mt-2">
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs capitalize">
                            {image.category}
                          </span>
                          <span className="text-xs text-gray-500">{formatFileSize(image.size)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Preview Modal */}
        {previewImage && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl max-h-full overflow-auto">
              <div className="flex justify-between items-center p-4 border-b">
                <div>
                  <h3 className="text-lg font-semibold">{previewImage.title || previewImage.originalName}</h3>
                  <p className="text-sm text-gray-500">Uploaded: {new Date(previewImage.uploadedAt).toLocaleDateString()}</p>
                </div>
                <div className="flex gap-2">
                  <a
                    href={previewImage.downloadURL}
                    download={previewImage.originalName}
                    className="p-2 text-gray-600 hover:text-gray-800"
                    title="Download"
                  >
                    <Download className="h-5 w-5" />
                  </a>
                  <button
                    onClick={() => setPreviewImage(null)}
                    className="p-2 text-gray-600 hover:text-gray-800"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="p-4">
                <img
                  src={previewImage.downloadURL}
                  alt={previewImage.title}
                  className="max-w-full max-h-96 mx-auto"
                />
                {previewImage.description && (
                  <p className="mt-4 text-gray-700">{previewImage.description}</p>
                )}
                <div className="flex justify-between items-center mt-4 pt-4 border-t text-sm text-gray-500">
                  <span>Category: <span className="capitalize">{previewImage.category}</span></span>
                  <span>Size: {formatFileSize(previewImage.size)}</span>
                  <span>Type: {previewImage.type}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GalleryManagement;